(() => {
  const DEFAULT_TAG = 'GVA Shade';

  const tagValue = (tags, prefix) => {
    const tag = (tags || []).find((item) => item.startsWith(prefix));
    return tag ? tag.slice(prefix.length).trim() : '';
  };

  // Colours read darkest-to-lightest like a real shade chart, so the grid is
  // grouped by category first. Sorting on the number alone interleaved blondes
  // with browns (…8N brown, 8/9 blonde, 9 blonde, 12C brown…).
  const CATEGORY_ORDER = { BROWN: 0, RED: 1, BLONDE: 2 };

  // Within a category: numbered shades by their base level (6, 6.2, 6C, 6N all
  // sit together at level 6), then named ones (Piano…, Ombre…, Cold Platinum)
  // alphabetically at the end.
  const shadeSortKey = (shade) => {
    // Families decorate colour codes differently: "1", "#1", "(#1)", "# 1B".
    // Strip the punctuation so every one of them still sorts by its number.
    const match = shade.replace(/^[(\[#\s]+/, '').match(/^(\d+)(?:\.(\d+))?/);
    if (!match) return [1, 0, 0];
    return [0, parseInt(match[1], 10), match[2] ? parseInt(match[2], 10) : 0];
  };

  const compareShades = (a, b) => {
    const ac = CATEGORY_ORDER[a.category] ?? 3;
    const bc = CATEGORY_ORDER[b.category] ?? 3;
    if (ac !== bc) return ac - bc;
    const [anamed, alevel, asub] = shadeSortKey(a.shade);
    const [bnamed, blevel, bsub] = shadeSortKey(b.shade);
    return anamed - bnamed || alevel - blevel || asub - bsub
      || a.shade.toUpperCase().localeCompare(b.shade.toUpperCase());
  };

  // /products.json only ever returns products published to the Online Store,
  // so unpublished shades simply never appear here.
  const fetchAllProducts = async () => {
    const products = [];
    for (let page = 1; page <= 10; page += 1) {
      const response = await fetch(`/products.json?limit=250&page=${page}`);
      if (!response.ok) throw new Error(`products.json failed: ${response.status}`);
      const batch = (await response.json()).products || [];
      products.push(...batch);
      if (batch.length < 250) break;
    }
    return products;
  };

  // Not cached on purpose: a category/shade tag edited in Shopify must be
  // reflected on the very next page load, and /products.json is uncached.
  // GVA 28.10.2026: список оттенков встроен в страницу (data-gva-shades), качаем
  // весь каталог только если его там нет. Повтор handle (текущий товар) отбрасываем.
  const embeddedProducts = (browser) => {
    try {
      const node = browser.querySelector('[data-gva-shades]');
      const list = node ? JSON.parse(node.textContent) : [];
      const seen = new Set();
      return list.filter((item) => item.handle && !seen.has(item.handle) && seen.add(item.handle));
    } catch (error) {
      return [];
    }
  };

  const loadShades = async (familyTag, browser) => {
    const embedded = embeddedProducts(browser).filter((product) => (product.tags || []).includes(familyTag));
    const source = embedded.length > 1
      ? embedded.map((product) => ({ ...product, images: [{ src: product.image }], variants: [{ available: product.available }] }))
      : await fetchAllProducts();
    const shades = source
      .filter((product) => (product.tags || []).includes(familyTag))
      .map((product) => ({
        handle: product.handle,
        url: `/products/${product.handle}`,
        shade: tagValue(product.tags, 'Shade:'),
        category: (tagValue(product.tags, 'Shade Category:') || '').toUpperCase(),
        texture: tagValue(product.tags, 'Texture:'),
        image: product.images?.[0]?.src || '',
        available: (product.variants || []).some((variant) => variant.available)
      }))
      .filter((shade) => shade.shade);

    return shades;
  };

  const init = async (browser) => {
    if (browser.dataset.ready === 'true') return;
    browser.dataset.ready = 'true';

    const grid = browser.querySelector('.gva-shade-browser__grid');
    const tabs = [...browser.querySelectorAll('.gva-shade-browser__tab')];
    const textureStrip = browser.querySelector('.gva-shade-browser__textures');
    const currentHandle = browser.dataset.currentHandle;
    const currentTexture = browser.dataset.currentTexture;
    const currentShade = browser.dataset.currentShade;
    const familyTag = browser.dataset.familyTag || DEFAULT_TAG;

    let all = [];
    try {
      all = await loadShades(familyTag, browser);
    } catch (error) {
      grid.innerHTML = '<span class="gva-shade-browser__empty">Shades could not be loaded. Please refresh the page.</span>';
      return;
    }

    // Only offer textures that actually have published shades, in a stable order.
    // Families without textures (Tapes) yield an empty list and skip the strip.
    const textureOrder = ['Straight', 'Beach Wave', 'Deep Wave'];
    const available = new Set(all.map((shade) => shade.texture).filter(Boolean));
    const textures = textureOrder.filter((texture) => available.has(texture))
      .concat([...available].filter((texture) => !textureOrder.includes(texture)).sort());
    const hasTextures = textures.length > 1;

    let activeTexture = textures.includes(currentTexture) ? currentTexture : textures[0];
    let activeCategory = 'ALL';

    // One tile per shade name within the chosen texture. If a shade repeats,
    // prefer the current product, then an in-stock one, so no duplicates appear.
    const shadesFor = (texture) => {
      const byShade = new Map();
      const pool = hasTextures ? all.filter((shade) => shade.texture === texture) : all;
      pool.forEach((shade) => {
        const key = shade.shade.toUpperCase();
        const seen = byShade.get(key);
        if (!seen) return void byShade.set(key, shade);
        if (shade.handle === currentHandle) return void byShade.set(key, shade);
        if (seen.handle !== currentHandle && !seen.available && shade.available) byShade.set(key, shade);
      });
      return [...byShade.values()].sort(compareShades);
    };

    const swatchSrc = (src) => {
      try {
        const u = new URL(src, location.href);
        // квадратик ~90px: обычный экран — 150px, плотный (телефон) — до 300px
        u.searchParams.set('width', String(Math.min(300, Math.ceil((110 * (window.devicePixelRatio || 1)) / 50) * 50)));
        return u.toString();
      } catch (e) {
        return src;
      }
    };
    const swatchObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.style.backgroundImage = entry.target.dataset.bg;
            obs.unobserve(entry.target);
          });
        }, { rootMargin: '400px 0px' })
      : null;

    const renderGrid = () => {
      const unique = shadesFor(activeTexture);
      const visible = activeCategory === 'ALL'
        ? unique
        : unique.filter((shade) => shade.category === activeCategory);

      // A category with nothing in this texture would be a dead end, so hide it.
      const categories = new Set(unique.map((shade) => shade.category).filter(Boolean));
      tabs.forEach((tab) => {
        const category = tab.dataset.category;
        tab.hidden = category !== 'ALL' && !categories.has(category);
      });

      grid.textContent = '';
      if (!visible.length) {
        grid.innerHTML = '<span class="gva-shade-browser__empty">No shades in this category.</span>';
        return;
      }

      const fragment = document.createDocumentFragment();
      visible.forEach((item) => {
        const isCurrent = item.handle === currentHandle;
        const link = document.createElement('a');
        link.className = 'gva-shade-browser__swatch';
        if (isCurrent) link.classList.add('is-current');
        if (!item.available) link.classList.add('is-soldout');
        link.href = item.url;
        link.title = item.available ? item.shade : `${item.shade} — sold out`;
        link.setAttribute('role', 'listitem');
        if (isCurrent) link.setAttribute('aria-current', 'page');
        // CSS url() needs quotes escaped, not JSON-encoded.
        // GVA 10.10.2026: квадратик ~90px, поэтому берём копию шириной 300 вместо оригинала (~0.8 МБ → ~50 КБ)
        // и ставим её, только когда квадратик подъезжает к экрану.
        if (item.image) {
          link.dataset.bg = `url("${swatchSrc(item.image).replace(/"/g, '%22')}")`;
          swatchObserver ? swatchObserver.observe(link) : (link.style.backgroundImage = link.dataset.bg);
        }

        const label = document.createElement('span');
        label.textContent = item.shade;
        link.append(label);
        fragment.append(link);
      });
      grid.append(fragment);
    };

    if (textureStrip && !hasTextures) textureStrip.hidden = true;

    if (textureStrip && hasTextures) {
      textureStrip.textContent = '';
      textures.forEach((texture) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'gva-shade-browser__texture';
        button.dataset.texture = texture;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', String(texture === activeTexture));
        button.textContent = texture;

        button.addEventListener('click', () => {
          activeTexture = texture;
          [...textureStrip.children].forEach((item) => {
            item.setAttribute('aria-selected', String(item === button));
          });
          // Staying on the same shade across textures is the whole point of the
          // switcher, so follow the shade when the chosen texture has it.
          const sameShade = shadesFor(texture)
            .find((shade) => shade.shade.toUpperCase() === (currentShade || '').toUpperCase());
          if (sameShade && sameShade.handle !== currentHandle) {
            window.location.href = sameShade.url;
            return;
          }
          renderGrid();
        });

        textureStrip.append(button);
      });
    }

    tabs.forEach((tab) => tab.addEventListener('click', () => {
      activeCategory = tab.dataset.category;
      tabs.forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
      renderGrid();
    }));

    renderGrid();
  };

  const start = () => document.querySelectorAll('[data-gva-shade-browser]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', start);
})();
